import styled from '@emotion/styled';
import isPropValid from '@emotion/is-prop-valid';

/**
 * Clickable card header for any collapsible section (Issues, Pending,
 * Sent, Live Events, Raw metadata). One pattern, one place to change it.
 *
 * Pair with <Chevron open={isOpen} />.
 *
 * Usage:
 *   <DetailCard>
 *     <CollapsibleHeader onClick={() => setOpen(o => !o)}>
 *       <h2>Issues</h2>
 *       <Row gap={2} align="center">
 *         <StatusChip variant="critical">2 issues</StatusChip>
 *         <Chevron open={open} />
 *       </Row>
 *     </CollapsibleHeader>
 *     {open && <CardBody>...</CardBody>}
 *   </DetailCard>
 */
export const CollapsibleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
  transition: ${({ theme }) => theme.animation.transition.color};

  /* Override DetailCard's h2 margin so the header line sits cleanly */
  h2 { margin: 0; }

  &:hover h2,
  &:hover {
    color: ${({ theme }) => theme.accent.primary};
  }
`;

interface ChevronProps {
  open: boolean;
}

/**
 * Animated chevron for collapsible headers. Pure visual — apply the
 * actual hide/show on the parent. Uses Tabler's chevron-down path.
 */
export const Chevron = styled('span', {
  shouldForwardProp: prop => isPropValid(prop) && prop !== 'open',
})<ChevronProps>`
  display: inline-flex;
  width: 20px;
  height: 20px;
  color: ${({ theme }) => theme.font.color.tertiary};
  transition: transform 0.15s ease, color 0.15s ease;
  transform: ${({ open }) => open ? 'rotate(180deg)' : 'rotate(0)'};

  &::after {
    /* SVG chevron via mask so currentColor is honored. Keeps this primitive
       icon-library-free; pages that need richer icons should import Tabler. */
    content: '';
    width: 100%;
    height: 100%;
    background-color: currentColor;
    -webkit-mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6l6 -6"/></svg>') center/contain no-repeat;
    mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6l6 -6"/></svg>') center/contain no-repeat;
  }
`;

/**
 * Body of a collapsible card after the header. Adds the gap the header
 * doesn't carry by itself.
 */
export const CardBody = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;
